import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { Num } from "../types";
import { fmt } from "../lib/format";

/*
 * Многопанельный график без внешних библиотек (ТЗ, раздел 3.1): цена сверху, индикаторы в подвалах,
 * общая ось времени, перекрестие, масштаб колесом, сдвиг перетаскиванием, клавиатура (← → + −).
 * Ось X — порядковый номер бара (без разрывов на выходные).
 */

export interface LineSeries {
  kind: "line";
  key: string;
  label: string;
  color: string;
  values: Num[];
  width?: number;
  dash?: string;
  /** Скрыть из легенды (служебная линия). */
  quiet?: boolean;
  /** Своя шкала (вторая ось справа, без делений): например, нетто поверх открытого интереса (P5). */
  axis?: "right";
  /** Ступенчатая линия (каналы, стопы, недельные ряды на дневном графике). */
  step?: boolean;
  /** Легенда показывает значение ровно на баре под курсором (или последнем), без подстановки прошлого: стоп
   *  закрытой сделки не должен висеть в легенде, когда позиции уже нет. */
  noCarry?: boolean;
  /** Текст в легенде, когда значения на баре нет (по умолчанию «—»). */
  emptyText?: string;
}

export interface HistSeries {
  kind: "hist";
  key: string;
  label: string;
  values: Num[];
  pos: string;
  neg: string;
}

export interface OhlcSeries {
  kind: "ohlc";
  key: string;
  label: string;
  open: Num[];
  high: Num[];
  low: Num[];
  close: Num[];
  up: string;
  down: string;
}

export type SeriesDef = LineSeries | HistSeries | OhlcSeries;

export interface Band {
  from: number;
  to: number;
  className: string;
}

export interface Span {
  start: number;
  end: number;
  className: string;
  title?: string;
}

/** Отрезок в координатах «бар — значение» (линии тренда на цене). */
export interface Segment {
  i1: number;
  v1: number;
  i2: number;
  v2: number;
  className: string;
  title?: string;
}

export interface Level {
  y: number;
  className?: string;
  /** Подпись числом у правого края (ТЗ 3.1: «пороги подписаны числом у правого края»). */
  label?: string;
}

/** Текстовая подпись у точки (ТЗ 3.2: «макс. за K лет» у точки). */
export interface Label {
  index: number;
  value: number;
  text: string;
  className?: string;
}

export interface Marker {
  index: number;
  direction: number;
  title: string;
  muted?: boolean;
}

export interface PaneDef {
  id: string;
  title: string;
  hint?: string;
  /** Раскрываемая подсказка «Как читать» под заголовком панели. */
  help?: ReactNode;
  height: number;
  series: SeriesDef[];
  fixed?: [number, number];
  bands?: Band[];
  /** Вертикальные полосы по индексам баров (включительно) — подсветка периодов. */
  spans?: Span[];
  segments?: Segment[];
  levels?: Level[];
  zero?: boolean;
  markers?: Marker[];
  labels?: Label[];
  format?: (v: number) => string;
}

interface View {
  start: number;
  end: number; // не включительно
}

const GUTTER = 84;
const PAD_Y = 8;
/** Панели выше базовой высоты: на большом экране график читается лучше. */
const HEIGHT_BOOST = 1.15;

/**
 * Масштаб графика вслед за шрифтом страницы (html font-size плавно растёт с шириной экрана, база — 18px): высоты
 * панелей, поле подписей оси, маркеры и подписи времени растут вместе с текстом.
 */
/** Корневой шрифт на ноутбуке (1440px): 15,3px. На нём масштаб графика = 1. */
const BASE_FS = 15.3;

function readScale(): number {
  if (typeof window === "undefined") return 1;
  const fs = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(fs) && fs > 0 ? fs / BASE_FS : 1;
}

function useUiScale(): number {
  const [s, setS] = useState(readScale);
  useEffect(() => {
    const on = () => setS(readScale());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return s;
}
const MIN_BARS = 16;

/** Вертикальная отметка события во всех панелях (экспирации). */
export interface VMark {
  index: number;
  title: string;
  className?: string;
}

export function ChartStack({
  dates,
  panes,
  initialBars,
  vmarks,
  minStart = 0,
}: {
  dates: string[];
  panes: PaneDef[];
  initialBars?: number;
  vmarks?: VMark[];
  /** Первый бар, который можно показать: более ранние — только разгон индикаторов (график не уходит левее). */
  minStart?: number;
}) {
  const n = dates.length;
  // Левая граница прокрутки; если после неё слишком мало баров — показываем всё.
  const lo = n - minStart >= MIN_BARS ? Math.max(0, minStart) : 0;
  const wrap = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hoverState, setHover] = useState<number | null>(null);
  // Диапазон хранится вместе с длиной ряда, для которого он посчитан. При смене ряда (дни → недели) первый кадр
  // рисуется ДО эффекта сброса: старый дневной диапазон (бары 1300–1500) на недельном ряду (~300 баров) читал
  // за концом массивов и ронял страницу. Поэтому чужой диапазон сразу подменяется начальным для нового ряда.
  const [viewState, setViewState] = useState<View & { n: number }>(() => ({ ...initialView(n, initialBars, lo), n }));
  const view: View = viewState.n === n ? viewState : initialView(n, initialBars, lo);
  const setView = useCallback(
    (u: View | ((v: View) => View)) =>
      setViewState((s) => {
        const cur = s.n === n ? s : initialView(n, initialBars, lo);
        const next = typeof u === "function" ? u(cur) : u;
        return { start: next.start, end: next.end, n };
      }),
    [n, initialBars, lo],
  );
  const hover = hoverState != null && hoverState >= view.start && hoverState < Math.min(view.end, n) ? hoverState : null;
  const drag = useRef<{ x: number; view: View } | null>(null);

  useEffect(() => {
    setView(initialView(n, initialBars, lo));
    setHover(null);
  }, [n, initialBars, lo, setView]);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(320, Math.floor(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ui = useUiScale();
  const plotW = width - Math.round(GUTTER * ui);
  const count = Math.max(1, view.end - view.start);
  const step = plotW / count;
  const xOf = useCallback((i: number) => (i - view.start + 0.5) * step, [view.start, step]);
  const indexAt = useCallback(
    (clientX: number) => {
      const el = wrap.current;
      if (!el) return null;
      const x = clientX - el.getBoundingClientRect().left;
      if (x < 0 || x > plotW) return null;
      const i = Math.floor(x / step) + view.start;
      return i >= view.start && i < view.end ? i : null;
    },
    [plotW, step, view],
  );

  const zoom = useCallback(
    (factor: number, anchor: number | null) => {
      setView((v) => {
        const len = v.end - v.start;
        const next = clamp(Math.round(len * factor), Math.min(MIN_BARS, n - lo), n - lo);
        const a = anchor ?? v.end - 1;
        const ratio = len > 0 ? (a - v.start) / len : 1;
        let start = Math.round(a - ratio * next);
        start = clamp(start, lo, Math.max(lo, n - next));
        return { start, end: start + next };
      });
    },
    [n, lo, setView],
  );

  const pan = useCallback(
    (bars: number) => {
      setView((v) => {
        const len = v.end - v.start;
        const start = clamp(v.start + bars, lo, Math.max(lo, n - len));
        return { start, end: start + len };
      });
    },
    [n, lo, setView],
  );

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        pan(Math.round(e.deltaX / Math.max(2, step)));
        return;
      }
      e.preventDefault();
      zoom(e.deltaY > 0 ? 1.15 : 1 / 1.15, indexAt(e.clientX));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoom, pan, indexAt, step]);

  const onPointerDown = (e: ReactPointerEvent) => {
    drag.current = { x: e.clientX, view };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    setHover(indexAt(e.clientX));
    const d = drag.current;
    if (!d) return;
    const bars = Math.round((d.x - e.clientX) / step);
    const len = d.view.end - d.view.start;
    const start = clamp(d.view.start + bars, lo, Math.max(lo, n - len));
    setView({ start, end: start + len });
  };
  const onPointerUp = () => {
    drag.current = null;
  };
  const onKey = (e: ReactKeyboardEvent) => {
    const k = e.key;
    if (k === "ArrowLeft") pan(-Math.max(1, Math.round(count / 10)));
    else if (k === "ArrowRight") pan(Math.max(1, Math.round(count / 10)));
    else if (k === "+" || k === "=") zoom(1 / 1.25, null);
    else if (k === "-") zoom(1.25, null);
    else if (k === "0") setView({ start: lo, end: n });
    else return;
    e.preventDefault();
  };

  const focusIndex = hover ?? view.end - 1;
  const timeTicks = useMemo(() => calendarTicks(dates, view, plotW), [dates, view, plotW]);

  if (n === 0) return <div className="chart-empty">Нет данных для графика.</div>;

  return (
    <div
      className="chart"
      ref={wrap}
      tabIndex={0}
      role="application"
      aria-label="График: колесо — масштаб, перетаскивание — сдвиг, стрелки и +/− с клавиатуры, 0 — вся история"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={() => {
        setHover(null);
        drag.current = null;
      }}
      onDoubleClick={() => setView(initialView(n, initialBars, lo))}
      onKeyDown={onKey}
    >
      {panes.map((p, k) => (
        <div key={p.id}>
          <Pane
            pane={p}
            width={width}
            view={view}
            xOf={xOf}
            step={step}
            hover={hover}
            focus={focusIndex}
            hovering={hover != null}
            vticks={timeTicks}
            vmarks={vmarks}
            ui={ui}
          />
          {k === 0 && panes.length > 1 && (
            <TimeAxis dates={dates} width={width} xOf={xOf} hover={hover} ticks={timeTicks} ui={ui} />
          )}
        </div>
      ))}
      <TimeAxis dates={dates} width={width} xOf={xOf} hover={hover} ticks={timeTicks} ui={ui} />
    </div>
  );
}

function Pane({
  pane,
  width,
  view,
  xOf,
  step,
  hover,
  focus,
  hovering,
  vticks,
  vmarks,
  ui,
}: {
  pane: PaneDef;
  width: number;
  view: View;
  xOf: (i: number) => number;
  step: number;
  hover: number | null;
  focus: number;
  hovering: boolean;
  vticks: TimeTick[];
  vmarks?: VMark[];
  ui: number;
}) {
  const h = Math.round(pane.height * ui * HEIGHT_BOOST);
  const plotW = width - Math.round(GUTTER * ui);
  const padY = PAD_Y * ui;
  const format = pane.format ?? defaultFormat;

  const [lo, hi] = useMemo(() => {
    if (pane.fixed) return pane.fixed;
    let mn = Infinity;
    let mx = -Infinity;
    const take = (v: Num) => {
      if (v == null) return;
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    };
    for (const s of pane.series) {
      if (s.kind === "line" && s.axis === "right") continue;
      for (let i = view.start; i < view.end; i++) {
        if (s.kind === "ohlc") {
          take(s.high[i]);
          take(s.low[i]);
        } else take(s.values[i]);
      }
    }
    if (pane.zero) {
      mn = Math.min(mn, 0);
      mx = Math.max(mx, 0);
    }
    if (!isFinite(mn) || !isFinite(mx)) return [0, 1] as [number, number];
    if (mn === mx) {
      mn -= 1;
      mx += 1;
    }
    const pad = (mx - mn) * 0.07;
    return [mn - pad, mx + pad] as [number, number];
  }, [pane, view]);

  // Вторая шкала — для линий axis: "right" (своя нормировка в видимом окне).
  const [lo2, hi2] = useMemo(() => {
    let mn = Infinity;
    let mx = -Infinity;
    for (const s of pane.series) {
      if (s.kind !== "line" || s.axis !== "right") continue;
      for (let i = view.start; i < view.end; i++) {
        const v = s.values[i];
        if (v == null) continue;
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
    }
    if (!isFinite(mn) || !isFinite(mx)) return [0, 1] as [number, number];
    if (mn === mx) {
      mn -= 1;
      mx += 1;
    }
    const pad = (mx - mn) * 0.07;
    return [mn - pad, mx + pad] as [number, number];
  }, [pane, view]);

  const yOf = (v: number) => padY + (1 - (v - lo) / (hi - lo)) * (h - 2 * padY);
  const yOf2 = (v: number) => padY + (1 - (v - lo2) / (hi2 - lo2)) * (h - 2 * padY);
  // Низкая панель (полоса тренда): шкала значений не нужна и не помещается — подписи наезжали друг на друга.
  // На остальных подписи оси не ближе 16px друг к другу.
  const ticks = h < 70 * ui ? [] : spaced(niceTicks(lo, hi, h > 140 ? 5 : 3), yOf, 16 * ui);
  // Подписи уровней (20 / 25, 80 / 90): если над линией места нет — подпись под линией.
  const levelLabels = (() => {
    const out = new Map<number, number>();
    const ls = (pane.levels ?? []).filter((l) => l.label && l.y > lo && l.y < hi).sort((a, b) => yOf(a.y) - yOf(b.y));
    let lastBase = -Infinity;
    for (const l of ls) {
      const line = yOf(l.y);
      let y = line - 3 * ui;
      if (y - 11 * ui < lastBase) y = line + 12 * ui;
      out.set(l.y, y);
      lastBase = y;
    }
    return out;
  })();

  const paths = useMemo(
    () => buildPaths(pane, view, xOf, step, yOf, yOf2),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pane, view, xOf, step, lo, hi, lo2, hi2, h],
  );

  return (
    <section className="pane" id={`pane-${pane.id}`}>
      <header className="pane-head">
        <h3 title={pane.hint}>{pane.title}</h3>
        <ul className="legend">
          {pane.series
            .filter((s) => !(s.kind === "line" && s.quiet))
            .map((s) => (
              <li key={s.key}>
                <i className={`swatch swatch-${s.kind}`} style={swatchStyle(s)} />
                {s.label}
                <b>{legendValue(s, focus, format, hovering ? focus : view.start)}</b>
              </li>
            ))}
        </ul>
      </header>
      {pane.help && (
        <details className="pane-help">
          <summary>Как читать этот график</summary>
          <div className="pane-help-body">{pane.help}</div>
        </details>
      )}
      <svg width={width} height={h} className="pane-svg" aria-hidden="true">
        {pane.bands?.map((b, i) => {
          const y1 = yOf(Math.min(hi, b.to));
          const y2 = yOf(Math.max(lo, b.from));
          return y2 > y1 ? <rect key={i} x={0} y={y1} width={plotW} height={y2 - y1} className={b.className} /> : null;
        })}
        {pane.spans
          ?.filter((sp) => sp.end >= view.start && sp.start < view.end)
          .map((sp, i) => {
            const x1 = Math.max(0, xOf(Math.max(sp.start, view.start)) - step / 2);
            const x2 = Math.min(plotW, xOf(Math.min(sp.end, view.end - 1)) + step / 2);
            return x2 > x1 ? (
              <rect key={`s${i}`} x={x1} y={0} width={x2 - x1} height={h} className={sp.className}>
                {sp.title && <title>{sp.title}</title>}
              </rect>
            ) : null;
          })}
        {vmarks
          ?.filter((m) => m.index >= view.start && m.index < view.end)
          .map((m, i) => (
            <line key={`x${m.index}-${i}`} x1={xOf(m.index)} x2={xOf(m.index)} y1={0} y2={h} className={m.className ?? "vmark"}>
              <title>{m.title}</title>
            </line>
          ))}
        {vticks.map((t) => (
          <line key={`v${t.index}`} x1={xOf(t.index)} x2={xOf(t.index)} y1={0} y2={h} className={t.minor ? "grid-v minor" : t.major ? "grid-v major" : "grid-v"} />
        ))}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={0} x2={plotW} y1={yOf(t)} y2={yOf(t)} className="grid" />
            <text x={plotW + 8 * ui} y={yOf(t) + 5 * ui} className="tick">
              {format(t)}
            </text>
          </g>
        ))}
        {pane.zero && lo < 0 && hi > 0 && <line x1={0} x2={plotW} y1={yOf(0)} y2={yOf(0)} className="zero" />}
        {pane.levels?.map((l) =>
          l.y > lo && l.y < hi ? (
            <g key={l.y}>
              <line x1={0} x2={plotW} y1={yOf(l.y)} y2={yOf(l.y)} className={l.className ?? "level"} />
              {l.label && (
                <text x={plotW - 4 * ui} y={levelLabels.get(l.y) ?? yOf(l.y) - 3 * ui} textAnchor="end" className="level-label">
                  {l.label}
                </text>
              )}
            </g>
          ) : null,
        )}
        {paths}
        {pane.segments && pane.segments.length > 0 && (
          <g clipPath={`url(#clip-${pane.id})`}>
            <defs>
              <clipPath id={`clip-${pane.id}`}>
                <rect x={0} y={0} width={plotW} height={h} />
              </clipPath>
            </defs>
            {pane.segments
              .filter((sg) => sg.i2 >= view.start && sg.i1 < view.end)
              .map((sg, k) => (
                <line key={`g${k}`} x1={xOf(sg.i1)} y1={yOf(sg.v1)} x2={xOf(sg.i2)} y2={yOf(sg.v2)} className={sg.className}>
                  {sg.title && <title>{sg.title}</title>}
                </line>
              ))}
          </g>
        )}
        {pane.markers
          ?.filter((m) => m.index >= view.start && m.index < view.end)
          .map((m, k) => {
            const x = xOf(m.index);
            const base = markerBase(pane, m);
            const off = 10 * ui, half = 5 * ui, tall = 8 * ui;
            const y = base == null ? (m.direction > 0 ? h - off : off) : yOf(base) + (m.direction > 0 ? off : -off);
            const d =
              m.direction > 0
                ? `M${x},${y - half} l${half},${tall} h${-2 * half}z`
                : `M${x},${y + half} l${half},${-tall} h${-2 * half}z`;
            return (
              <path key={k} d={d} className={`marker ${m.direction > 0 ? "up" : "down"} ${m.muted ? "muted" : ""}`}>
                <title>{m.title}</title>
              </path>
            );
          })}
        {layoutLabels(
          (pane.labels ?? []).filter((lb) => lb.index >= view.start && lb.index < view.end && lb.value > lo && lb.value < hi),
          xOf,
          yOf,
          (lo + hi) / 2,
          plotW,
          h,
          ui,
        ).map((lb, k) => (
          <text key={`l${k}`} x={lb.x} y={lb.y} textAnchor={lb.anchor} className={lb.className ?? "point-label"}>
            {lb.text}
          </text>
        ))}
        {hover != null && <line x1={xOf(hover)} x2={xOf(hover)} y1={0} y2={h} className="crosshair" />}
        <line x1={plotW} x2={plotW} y1={0} y2={h} className="axis" />
      </svg>
    </section>
  );
}

/**
 * Раскладка подписей точек без наложений: подпись, которая пересеклась бы с уже поставленной, сдвигается на строку
 * дальше от линии (вверх — для подписей над точкой, вниз — для подписей под ней). У правого края подпись
 * выравнивается вправо, чтобы не заходить на шкалу цен.
 */
function layoutLabels(
  labels: Label[],
  xOf: (i: number) => number,
  yOf: (v: number) => number,
  mid: number,
  plotW: number,
  h: number,
  ui: number,
) {
  const lineH = 16 * ui;
  const placed: { x1: number; x2: number; y1: number; y2: number }[] = [];
  const out: (Label & { x: number; y: number; anchor: "middle" | "end" })[] = [];
  for (const lb of [...labels].sort((a, b) => a.index - b.index)) {
    const x0 = xOf(lb.index);
    const nearEdge = x0 > plotW - 90 * ui;
    const x = nearEdge ? Math.min(x0, plotW - 4 * ui) : x0;
    const w = lb.text.length * 7.2 * ui + 6 * ui; // оценка ширины текста (≈ 13px шрифт)
    const x1 = nearEdge ? x - w : x - w / 2;
    const x2 = nearEdge ? x : x + w / 2;
    const below = lb.value >= mid;
    let y = yOf(lb.value) + (below ? 16 * ui : -8 * ui);
    for (let guard = 0; guard < 6; guard++) {
      const hit = placed.some((p) => x1 < p.x2 && p.x1 < x2 && y - lineH + 3 * ui < p.y2 && p.y1 < y + 3 * ui);
      if (!hit) break;
      y += below ? lineH : -lineH;
    }
    y = Math.max(lineH, Math.min(h - 3 * ui, y));
    placed.push({ x1, x2, y1: y - lineH + 3 * ui, y2: y + 3 * ui });
    out.push({ ...lb, x, y, anchor: nearEdge ? "end" : "middle" });
  }
  return out;
}

function markerBase(pane: PaneDef, m: Marker): number | null {
  const s = pane.series[0];
  if (!s) return null;
  if (s.kind === "ohlc") return (m.direction > 0 ? s.low[m.index] : s.high[m.index]) ?? null;
  return s.values[m.index] ?? null;
}

function buildPaths(
  pane: PaneDef,
  view: View,
  xOf: (i: number) => number,
  step: number,
  yMain: (v: number) => number,
  yRight: (v: number) => number,
) {
  const yOf = yMain;
  return pane.series.map((s) => {
    if (s.kind === "line") {
      const y = s.axis === "right" ? yRight : yMain;
      let d = "";
      let pen = false;
      for (let i = view.start; i < view.end; i++) {
        const v = s.values[i];
        if (v == null) {
          pen = false;
          continue;
        }
        const yy = y(v);
        if (s.step && pen) {
          // ступенька: уровень держится до бара, где сменился (null рвёт линию — ТЗ 3.1)
          d += `H${(xOf(i) - step / 2).toFixed(1)}V${yy.toFixed(1)}H${xOf(i).toFixed(1)}`;
        } else {
          d += `${pen ? "L" : "M"}${xOf(i).toFixed(1)},${yy.toFixed(1)}`;
        }
        pen = true;
      }
      return (
        <path
          key={s.key}
          d={d}
          fill="none"
          stroke={s.color}
          strokeWidth={s.width ?? 1.6}
          strokeDasharray={s.dash}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      );
    }
    if (s.kind === "hist") {
      const w = Math.max(1, step * 0.72);
      let pos = "";
      let neg = "";
      const y0 = yOf(0);
      for (let i = view.start; i < view.end; i++) {
        const v = s.values[i];
        if (v == null) continue;
        const x = xOf(i) - w / 2;
        const y = yOf(v);
        const seg = `M${x.toFixed(1)},${y0.toFixed(1)}V${y.toFixed(1)}h${w.toFixed(1)}V${y0.toFixed(1)}z`;
        if (v >= 0) pos += seg;
        else neg += seg;
      }
      return (
        <g key={s.key}>
          <path d={pos} fill={s.pos} />
          <path d={neg} fill={s.neg} />
        </g>
      );
    }
    // OHLC-бары (предпочтение автора, гл. 14): вертикаль high–low, засечки open (слева) и close (справа).
    const tick = Math.max(0, Math.min(6, step * 0.38));
    let up = "";
    let down = "";
    for (let i = view.start; i < view.end; i++) {
      const o = s.open[i];
      const hgh = s.high[i];
      const l = s.low[i];
      const c = s.close[i];
      if (hgh == null || l == null || c == null) continue;
      const x = xOf(i);
      let seg = `M${x.toFixed(1)},${yOf(hgh).toFixed(1)}V${yOf(l).toFixed(1)}`;
      if (tick >= 1.5) {
        if (o != null) seg += `M${(x - tick).toFixed(1)},${yOf(o).toFixed(1)}H${x.toFixed(1)}`;
        seg += `M${x.toFixed(1)},${yOf(c).toFixed(1)}H${(x + tick).toFixed(1)}`;
      }
      if (o == null || c >= o) up += seg;
      else down += seg;
    }
    const sw = step > 6 ? 1.6 : 1.1;
    return (
      <g key={s.key} strokeWidth={sw} fill="none">
        <path d={up} stroke={s.up} />
        <path d={down} stroke={s.down} />
      </g>
    );
  });
}

export interface TimeTick {
  index: number;
  label: string;
  major: boolean;
  /** Деление без подписи (более мелкая гранулярность). */
  minor: boolean;
}

const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/**
 * Календарные подписи оси времени: недели → месяцы → кварталы → годы, самая мелкая гранулярность,
 * при которой подписи не налезают друг на друга (не ближе ~72 px).
 */
function calendarTicks(dates: string[], view: View, plotW: number): TimeTick[] {
  const count = view.end - view.start;
  if (count <= 0) return [];
  const maxLabels = Math.max(2, Math.floor(plotW / 86));
  type Level = { key: (d: Date) => string; label: (d: Date) => string; major: (d: Date) => boolean };
  const weekKey = (d: Date) => {
    const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const day = (t.getUTCDay() + 6) % 7; // понедельник = 0
    t.setUTCDate(t.getUTCDate() - day);
    return t.toISOString().slice(0, 10);
  };
  const dd = (n: number) => String(n).padStart(2, "0");
  const yy = (d: Date) => String(d.getUTCFullYear()).slice(2);
  const levels: Level[] = [
    { key: weekKey, label: (d) => `${dd(d.getUTCDate())}.${dd(d.getUTCMonth() + 1)}`, major: (d) => d.getUTCDate() <= 7 },
    {
      key: (d) => `${d.getUTCFullYear()}-${d.getUTCMonth()}`,
      label: (d) => (d.getUTCMonth() === 0 ? `${MONTHS[0]} ${yy(d)}` : MONTHS[d.getUTCMonth()]),
      major: (d) => d.getUTCMonth() === 0,
    },
    {
      key: (d) => `${d.getUTCFullYear()}-${Math.floor(d.getUTCMonth() / 3)}`,
      label: (d) => `${MONTHS[d.getUTCMonth()]} ${yy(d)}`,
      major: (d) => d.getUTCMonth() < 3,
    },
    { key: (d) => String(d.getUTCFullYear()), label: (d) => String(d.getUTCFullYear()), major: () => true },
  ];
  const parsed: Date[] = [];
  for (let i = view.start; i < view.end; i++) parsed.push(new Date(dates[i] + "T00:00:00Z"));
  const boundaries = (lv: Level): TimeTick[] => {
    const out: TimeTick[] = [];
    let prev = view.start > 0 ? lv.key(new Date(dates[view.start - 1] + "T00:00:00Z")) : null;
    for (let k = 0; k < parsed.length; k++) {
      const key = lv.key(parsed[k]);
      if (key !== prev) {
        out.push({ index: view.start + k, label: lv.label(parsed[k]), major: lv.major(parsed[k]), minor: false });
        prev = key;
      }
    }
    return out;
  };
  for (let li = 0; li < levels.length; li++) {
    const out = boundaries(levels[li]);
    if (out.length > maxLabels) continue;
    // Более мелкие деления без подписей (недели под месяцами, месяцы под кварталами), если помещаются.
    if (li > 0) {
      const finer = boundaries(levels[li - 1]);
      if (finer.length * 10 <= plotW) {
        const labeled = new Set(out.map((t) => t.index));
        for (const t of finer) if (!labeled.has(t.index)) out.push({ ...t, label: "", major: false, minor: true });
        out.sort((a, b) => a.index - b.index);
      }
    }
    return out;
  }
  // Очень длинная история: каждый N-й год.
  const all = boundaries(levels[3]);
  const every = Math.ceil(all.length / maxLabels);
  return all.filter((_, i) => i % every === 0);
}

function TimeAxis({
  dates,
  width,
  xOf,
  hover,
  ticks,
  ui,
}: {
  dates: string[];
  width: number;
  xOf: (i: number) => number;
  hover: number | null;
  ticks: TimeTick[];
  ui: number;
}) {
  const plotW = width - Math.round(GUTTER * ui);
  const h = Math.round(30 * ui);
  return (
    <svg width={width} height={h} className="time-axis" aria-hidden="true">
      <line x1={0} x2={plotW} y1={0.5} y2={0.5} className="axis" />
      {ticks.map((t) => (
        <g key={`${t.index}${t.minor ? "m" : ""}`}>
          <line x1={xOf(t.index)} x2={xOf(t.index)} y1={0} y2={(t.minor ? 3 : 6) * ui} className="axis" />
          {!t.minor && (
            <text x={xOf(t.index)} y={21 * ui} className={t.major ? "tick tick-major" : "tick"} textAnchor="middle">
              {t.label}
            </text>
          )}
        </g>
      ))}
      {hover != null && (
        <g>
          <rect x={xOf(hover) - 50 * ui} y={3 * ui} width={100 * ui} height={24 * ui} rx={7 * ui} className="time-badge" />
          <text x={xOf(hover)} y={20 * ui} textAnchor="middle" className="time-badge-text">
            {fmt.date(dates[hover])}
          </text>
        </g>
      )}
    </svg>
  );
}

/** Значение в точке; без наведения — последнее доступное значение в видимом окне. */
function legendValue(s: SeriesDef, i: number, format: (v: number) => string, floor: number) {
  const arr = s.kind === "ohlc" ? s.close : s.values;
  if (s.kind === "line" && s.noCarry) {
    const v = arr[i];
    return v != null ? format(v) : (s.emptyText ?? "—");
  }
  for (let k = i; k >= floor; k--) {
    const v = arr[k];
    if (v != null) return format(v);
    if (k === floor || floor === i) break;
  }
  return "—";
}

function swatchStyle(s: SeriesDef): CSSProperties {
  if (s.kind === "line") return { background: s.color };
  if (s.kind === "hist") return { background: `linear-gradient(90deg, ${s.pos} 50%, ${s.neg} 50%)` };
  return { background: `linear-gradient(90deg, ${s.up} 50%, ${s.down} 50%)` };
}

function defaultFormat(v: number) {
  const a = Math.abs(v);
  if (a >= 1e6) return fmt.compact(v);
  if (a >= 100) return fmt.int(v);
  return v.toFixed(a >= 10 ? 1 : 2).replace(".", ",");
}

/** Убирает подписи оси, стоящие ближе minGap пикселей к предыдущей. */
function spaced(ticks: number[], yOf: (v: number) => number, minGap: number): number[] {
  const out: number[] = [];
  for (const t of ticks) if (!out.length || Math.abs(yOf(out[out.length - 1]) - yOf(t)) >= minGap) out.push(t);
  return out;
}

function niceTicks(lo: number, hi: number, count: number): number[] {
  const span = hi - lo;
  if (!(span > 0)) return [];
  const raw = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const stepN = norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1;
  const st = stepN * mag;
  const out: number[] = [];
  for (let t = Math.ceil(lo / st) * st; t <= hi; t += st) out.push(Number(t.toPrecision(12)));
  return out;
}

function initialView(n: number, bars?: number, lo = 0): View {
  const len = clamp(bars ?? n - lo, Math.min(MIN_BARS, n - lo), n - lo);
  return { start: Math.max(lo, n - len), end: n };
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}
